import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  fetchContractorAvailability,
  fetchContractorAvailabilityBatch,
  fetchOrgContractorAvailability,
  type ContractorAvailabilityRow,
} from "@/services/contractor-availability.service";

/**
 * Fetches availability rows for a single contractor over a date range.
 * Returns an empty array when contractorId is null (no contractor selected).
 *
 * Usage: dispatch calendar — overlay day cells when a contractor is selected.
 */
export function useContractorAvailability(
  contractorId: string | null,
  from: Date,
  to: Date,
): { data: ContractorAvailabilityRow[]; isLoading: boolean } {
  const fromStr = format(from, "yyyy-MM-dd");
  const toStr = format(to, "yyyy-MM-dd");

  const query = useQuery({
    queryKey: ["contractor-availability", contractorId, fromStr, toStr],
    queryFn: () =>
      fetchContractorAvailability(contractorId!, fromStr, toStr),
    enabled: !!contractorId,
    staleTime: 60_000,
  });

  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
  };
}

/**
 * Fetches all org availability for a date range.
 * Returns a nested Map<contractorId, Map<date, { is_available, note }>>
 * so the calendar can tint only the specific contractor's row on each date.
 */
export function useOrgContractorAvailability(
  from: Date,
  to: Date,
): { availabilityByContractor: Map<string, Map<string, { is_available: boolean; note: string | null; installer_name: string | null }>>; isLoading: boolean } {
  const fromStr = format(from, "yyyy-MM-dd");
  const toStr = format(to, "yyyy-MM-dd");

  const query = useQuery({
    queryKey: ["org-contractor-availability", fromStr, toStr],
    queryFn: () => fetchOrgContractorAvailability(fromStr, toStr),
    staleTime: 30_000,
  });

  const availabilityByContractor = useMemo(() => {
    const map = new Map<string, Map<string, { is_available: boolean; note: string | null; installer_name: string | null }>>();
    for (const row of query.data ?? []) {
      if (!map.has(row.contractor_id)) {
        map.set(row.contractor_id, new Map());
      }
      map.get(row.contractor_id)!.set(row.date, { is_available: row.is_available, note: row.note, installer_name: row.installer_name });
    }
    return map;
  }, [query.data]);

  return { availabilityByContractor, isLoading: query.isLoading };
}

/**
 * Fetches availability for a list of contractors on a single date.
 * Returns a Map<contractorId, ContractorAvailabilityRow> for O(1) lookups.
 *
 * Usage: ContractorInterestQueue — check before sending an offer.
 */
export function useContractorAvailabilityBatch(
  contractorIds: string[],
  date: string | null,
): { availabilityMap: Map<string, ContractorAvailabilityRow>; isLoading: boolean } {
  const sortedIds = useMemo(() => [...contractorIds].sort().join(","), [contractorIds]);

  const query = useQuery({
    queryKey: ["contractor-availability-batch", sortedIds, date],
    queryFn: () => fetchContractorAvailabilityBatch(contractorIds, date!),
    enabled: contractorIds.length > 0 && !!date,
    staleTime: 60_000,
  });

  const availabilityMap = useMemo(() => {
    const map = new Map<string, ContractorAvailabilityRow>();
    for (const row of query.data ?? []) {
      map.set(row.contractor_id, { date: row.date, is_available: row.is_available, note: row.note, installer_name: row.installer_name });
    }
    return map;
  }, [query.data]);

  return { availabilityMap, isLoading: query.isLoading };
}
